import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supportService } from '../services/api/supportService';
import type {
  GetMyTicketsParams,
  CreateTicketPayload,
  AddCommentPayload,
  RateTicketPayload,
} from '../types/support';
import toast from 'react-hot-toast';

export interface UploadedFile {
  name: string;
  blobPath: string;
}

const KEYS = {
  modules: ['support', 'modules'] as const,
  myTickets: (params?: GetMyTicketsParams) => ['support', 'my-tickets', params] as const,
  ticketDetail: (id: number) => ['support', 'ticket', id] as const,
};

export function useSupport() {
  const queryClient = useQueryClient();

  const useModules = () =>
    useQuery({
      queryKey: KEYS.modules,
      queryFn: () => supportService.getModules().then((r) => r.data.data ?? []),
      staleTime: 3_600_000,
    });

  const useMyTickets = (params?: GetMyTicketsParams) =>
    useQuery({
      queryKey: KEYS.myTickets(params),
      queryFn: () => supportService.getMyTickets(params).then((r) => r.data),
    });

  const useTicketDetail = (id: number | null) =>
    useQuery({
      queryKey: KEYS.ticketDetail(id!),
      queryFn: () => supportService.getTicketDetail(id!).then((r) => r.data.data),
      enabled: !!id,
    });

  const useCreateTicket = () =>
    useMutation({
      mutationFn: (payload: CreateTicketPayload) => supportService.createTicket(payload),
      onSuccess: () => {
        toast.success('Support request submitted');
        queryClient.invalidateQueries({ queryKey: ['support', 'my-tickets'] });
      },
      onError: () => toast.error('Failed to submit support request'),
    });

  const useAddComment = () =>
    useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: AddCommentPayload }) =>
        supportService.addComment(id, payload),
      onSuccess: (_, { id }) => {
        toast.success('Reply sent');
        queryClient.invalidateQueries({ queryKey: KEYS.ticketDetail(id) });
      },
      onError: () => toast.error('Failed to send reply'),
    });

  const useReopenTicket = () =>
    useMutation({
      mutationFn: (id: number) => supportService.reopenTicket(id),
      onSuccess: (_, id) => {
        toast.success('Ticket reopened');
        queryClient.invalidateQueries({ queryKey: KEYS.ticketDetail(id) });
        queryClient.invalidateQueries({ queryKey: ['support', 'my-tickets'] });
      },
      onError: () => toast.error('Failed to reopen ticket'),
    });

  const useRateTicket = () =>
    useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: RateTicketPayload }) =>
        supportService.rateTicket(id, payload),
      onSuccess: (_, { id }) => {
        toast.success('Thank you for your feedback!');
        queryClient.invalidateQueries({ queryKey: KEYS.ticketDetail(id) });
      },
      onError: () => toast.error('Failed to submit rating'),
    });

  const useUploadAttachment = () => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    const upload = async (file: File) => {
      setIsUploading(true);
      try {
        const res = await supportService.getUploadUrl(file.type || 'application/octet-stream');
        const { uploadUrl, blobPath } = res.data.data;
        await supportService.uploadFileDirect(uploadUrl, file);
        setFiles((prev) => [...prev, { name: file.name, blobPath }]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setIsUploading(false);
      }
    };

    const remove = (blobPath: string) =>
      setFiles((prev) => prev.filter((f) => f.blobPath !== blobPath));

    const clear = () => setFiles([]);

    const toJson = () =>
      files.length
        ? JSON.stringify(files.map((f) => ({ path: f.blobPath, name: f.name })))
        : undefined;

    return { files, isUploading, upload, remove, clear, toJson };
  };

  return {
    useModules,
    useMyTickets,
    useTicketDetail,
    useCreateTicket,
    useAddComment,
    useReopenTicket,
    useRateTicket,
    useUploadAttachment,
  };
}
